import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { googleBewertungAPI } from '@/lib/api';

/* Google-Bewertung (Betreiber 18.10.2026): Die öffentliche Seite /probetraining zeigt
   „★★★★★ 4,9 aus 120 Google-Bewertungen“ — nur mit diesem Eintrag, nichts wird geschätzt.
   Pflegen darf nur die Leitung (admin/manager); der Server prüft das selbst. */

const fehlerText = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : 'Speichern fehlgeschlagen';
};

function zeitpunkt(iso) {
  if (!iso) return '';
  let s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function GoogleBewertungKarte() {
  const [stand, setStand] = useState(null);
  const [sterne, setSterne] = useState('');
  const [anzahl, setAnzahl] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    googleBewertungAPI.get().then((r) => {
      const d = r.data || {};
      setStand(d);
      setSterne(typeof d.sterne === 'number' ? String(d.sterne).replace('.', ',') : '');
      setAnzahl(typeof d.anzahl === 'number' ? String(d.anzahl) : '');
    }).catch(() => setStand({}));
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const darf = !!stand?.darf_aendern;
  const speichern = async () => {
    const s = parseFloat(String(sterne).replace(',', '.'));
    const a = parseInt(anzahl, 10);
    if (!Number.isFinite(s) || !Number.isFinite(a)) { toast.error('Sterne und Anzahl angeben'); return; }
    setLaeuft(true);
    try { await googleBewertungAPI.setzen({ sterne: s, anzahl: a }); toast.success('Google-Bewertung gespeichert'); laden(); }
    catch (e) { toast.error(fehlerText(e)); }
    finally { setLaeuft(false); }
  };
  const entfernen = async () => {
    setLaeuft(true);
    try { await googleBewertungAPI.entfernen(); toast.success('Eintrag entfernt — die Seite zeigt keine Bewertung mehr'); laden(); }
    catch (e) { toast.error(fehlerText(e)); }
    finally { setLaeuft(false); }
  };

  return (
    <Card className="border-border/60 mt-4" data-testid="google-bewertung-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Star className="w-4 h-4" /> Google-Bewertung auf öffentlichen Seiten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Erscheint auf /probetraining als „★★★★★ 4,9 aus 120 Google-Bewertungen“. Bitte die echten Werte aus dem Google-Profil
          übernehmen. Ohne Eintrag zeigt die Seite keine Bewertung.{!darf && ' Ändern kann nur die Leitung.'}
        </p>
        <div className="grid grid-cols-2 gap-3 max-w-sm">
          <div>
            <Label className="text-xs">Sterne (1,0–5,0)</Label>
            <Input inputMode="decimal" value={sterne} onChange={(e) => setSterne(e.target.value)} disabled={!darf || laeuft} placeholder="4,9" data-testid="google-bewertung-sterne" />
          </div>
          <div>
            <Label className="text-xs">Anzahl Bewertungen</Label>
            <Input inputMode="numeric" value={anzahl} onChange={(e) => setAnzahl(e.target.value)} disabled={!darf || laeuft} placeholder="120" data-testid="google-bewertung-anzahl" />
          </div>
        </div>
        {stand?.aktualisiert_am && (
          <p className="text-[11px] text-muted-foreground">Zuletzt geändert {zeitpunkt(stand.aktualisiert_am)}{stand.aktualisiert_von ? ` von ${String(stand.aktualisiert_von)}` : ''}</p>
        )}
        {darf && (
          <div className="flex gap-2">
            <Button size="sm" onClick={speichern} disabled={laeuft} data-testid="google-bewertung-speichern">Speichern</Button>
            {typeof stand?.sterne === 'number' && <Button size="sm" variant="outline" onClick={entfernen} disabled={laeuft}>Entfernen</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
